import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FEATURE_FLAGS } from '../config/flags';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePublisher?: boolean;
  requireAuth?: boolean;
}

export default function ProtectedRoute({
  children,
  requirePublisher = false,
  requireAuth = true,
}: ProtectedRouteProps) {
  const { user, publisher, initializing, publisherLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Dev: unlock routes regardless of auth/publisher/loading when flag enabled
  if (FEATURE_FLAGS?.UNLOCK_PARTNER_ROUTES) {
    return <>{children}</>;
  }

  if (initializing) {
    return (
      <div className='flex min-h-screen items-center justify-center'>
        <div className='flex flex-col items-center gap-4'>
          <Loader2 className='h-8 w-8 animate-spin text-primary' />
          <p className='text-sm text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  // Must be authenticated
  if (requireAuth && !isAuthenticated()) {
    return <Navigate to='/auth' state={{ from: location }} replace />;
  }

  // Publisher portal routes
  if (requirePublisher) {
    if (!isAuthenticated()) {
      return <Navigate to='/auth' state={{ from: location }} replace />;
    }

    // Wait for publisher fetch before gating — avoids flashing onboarding on reload
    if (publisherLoading) {
      return (
        <div className='flex min-h-screen items-center justify-center'>
          <div className='flex flex-col items-center gap-4'>
            <Loader2 className='h-8 w-8 animate-spin text-primary' />
            <p className='text-sm text-muted-foreground'>Loading...</p>
          </div>
        </div>
      );
    }

    // No publisher record → start onboarding from the beginning
    if (!publisher) {
      return <Navigate to='/onboarding' state={{ from: location }} replace />;
    }

    // Has publisher but contract not yet signed → gate the dashboard.
    // Guild-founded studios are probationary: portal access now, contract gates publishing.
    if (!publisher.contract_signed && publisher.origin !== 'guild') {
      // If they've filled in business info, go to contract step
      if (publisher.application_completed) {
        return (
          <Navigate
            to='/onboarding?step=contract'
            state={{ from: location }}
            replace
          />
        );
      }
      // Otherwise go to step 1
      return <Navigate to='/onboarding' state={{ from: location }} replace />;
    }

    // Suspended or rejected — show a blocked state via onboarding
    if (publisher.status === 'rejected') {
      return <Navigate to='/onboarding?step=rejected' state={{ from: location }} replace />;
    }
    if (publisher.status === 'suspended') {
      return <Navigate to='/onboarding?step=suspended' state={{ from: location }} replace />;
    }

    // Contract signed → full portal access
  }

  return <>{children}</>;
}

export const withAuth = (Component: React.ComponentType) => {
  return (props: any) => (
    <ProtectedRoute requireAuth>
      <Component {...props} />
    </ProtectedRoute>
  );
};

export const withPublisher = (Component: React.ComponentType) => {
  return (props: any) => (
    <ProtectedRoute requireAuth requirePublisher>
      <Component {...props} />
    </ProtectedRoute>
  );
};
